import { AudioPlayerCanvasWrapper } from '@/client/audio-player/audio-player-canvas-wrapper';

interface Track {
  id: string;
  title: string;
  artists: { id: string; name: string }[];
  previewURL: string;
}

type AudioPlayerStatus = 'idle' | 'loading' | 'loaded' | 'error';

export class AudioPlayer {
  private audioContext: AudioContext | null = null;
  private canvasWrapper: AudioPlayerCanvasWrapper = new AudioPlayerCanvasWrapper();
  private track: Track | null = null;
  private audio: HTMLAudioElement | null = null;
  private isPlaying: boolean = false;
  private currentTime: number = 0;
  private error: Error | null = null;
  private status: AudioPlayerStatus = 'idle';
  private destroyed: boolean = false;
  private onTimeUpdateCallbacks: Set<(time: number) => void> = new Set();
  private onStatusUpdateCallbacks: Set<(status: AudioPlayerStatus) => void> = new Set();
  private onPlaybackStatusUpdateCallbacks: Set<(isPlaying: boolean) => void> = new Set();

  private handleTimeUpdate = () => {
    if (this.audio) {
      this.currentTime = this.audio.currentTime;
      this.canvasWrapper.setPlayLocation(this.currentTime);
      this.onTimeUpdateCallbacks.forEach((callback) => callback(this.currentTime));
    }
  };

  private handleEnded = () => {
    this.isPlaying = false;
    if (this.audio) this.audio.currentTime = 0;
    this.onPlaybackStatusUpdateCallbacks.forEach((callback) => callback(this.isPlaying));
  };

  private handleError = (event: ErrorEvent) => {
    this.error = event.error;
  };

  private attachAudioListeners(audio: HTMLAudioElement) {
    audio.addEventListener('timeupdate', this.handleTimeUpdate.bind(this));
    audio.addEventListener('ended', this.handleEnded.bind(this));
    audio.addEventListener('error', this.handleError.bind(this));
  }

  private detachAudioListeners(audio: HTMLAudioElement) {
    audio.removeEventListener('timeupdate', this.handleTimeUpdate.bind(this));
    audio.removeEventListener('ended', this.handleEnded.bind(this));
    audio.removeEventListener('error', this.handleError.bind(this));
  }

  constructor() {
    this.canvasWrapper.addOnClickCallback(() => {
      this.audio?.pause();
    });

    const cb = (loc: number) => {
      if (!this.audio || this.audio.readyState < 4) {
        return;
      }

      this.audio.currentTime = loc;

      if (this.isPlaying && this.audio.paused) {
        this.audio.play();
      }
    };
    this.canvasWrapper.addOnLocationUpdateCallback(cb.bind(this));
  }

  play() {
    if (this.destroyed) return;
    if (this.audio && this.status === 'loaded' && !this.isPlaying) {
      this.audio.play();
      this.isPlaying = true;
      this.onPlaybackStatusUpdateCallbacks.forEach((callback) => callback(this.isPlaying));
    }
  }

  pause() {
    if (this.destroyed) return;
    if (this.audio && this.status === 'loaded' && this.isPlaying) {
      this.isPlaying = false;
      this.audio.pause();
      this.onPlaybackStatusUpdateCallbacks.forEach((callback) => callback(this.isPlaying));
    }
  }

  stop() {
    if (this.destroyed) return;
    if (this.audio && this.status === 'loaded') {
      this.pause();
      this.currentTime = 0;
      this.audio.currentTime = 0;
      this.onPlaybackStatusUpdateCallbacks.forEach((callback) => callback(this.isPlaying));
    }
  }

  private setStatus(status: AudioPlayerStatus): void {
    this.status = status;
    this.onStatusUpdateCallbacks.forEach((cb) => cb(this.status));
  }

  setCanvas(canvas: HTMLCanvasElement | null) {
    if (this.destroyed) {
      return;
    }
    this.canvasWrapper.setCanvas(canvas);
  }

  isCurrentlyPlaying() {
    return !this.destroyed && this.isPlaying;
  }

  setAudioElement(audio: HTMLAudioElement | null) {
    if (this.destroyed) return;
    if (this.audio) {
      this.detachAudioListeners(this.audio);
    }
    this.audio = audio;
    if (this.audio) {
      this.attachAudioListeners(this.audio);
    }
  }

  setSmoothingFactor(factor: number) {
    if (this.destroyed) return;
    this.canvasWrapper.setSmoothingFactor(factor);
  }

  setWaveformHeightFactor(factor: number) {
    if (this.destroyed) return;
    this.canvasWrapper.setWaveformHeightFactor(factor);
  }

  setAudioContext(context: AudioContext) {
    if (this.destroyed) return;
    this.audioContext = context;
    this.canvasWrapper.setAudioContext(context);
  }

  setTrack(track: Track | null | undefined, onLoad?: (track: Track) => void) {
    if (this.destroyed) return;
    this.setStatus('idle');
    this.track = track ? track : null;
    if (!this.audio || !this.track) return;

    const targetTrack = this.track;
    this.setStatus('loading');

    this.audio.src = this.track.previewURL;
    this.audio.load();
    this.canvasWrapper.clear();

    const url = this.track.previewURL;

    fetch(url).then(async (res) => {
      if (!res.ok) {
        if (this.track) {
          this.error = new Error(`failed to retrieve audio from ${url}`);
        }
        return;
      }
      this.canvasWrapper.readAudio(url);
      if (this.isPlaying && this.audio) {
        this.audio.play();
      }

      if (onLoad) {
        onLoad(targetTrack);
      }

      this.setStatus('loaded');
    });
  }

  getCurrentTime() {
    if (this.destroyed) return -1;
    return this.currentTime;
  }

  getTrack() {
    if (this.destroyed) return null;
    return this.track;
  }

  getError() {
    return this.error;
  }

  getStatus() {
    return this.status;
  }

  isReady() {
    if (this.destroyed) return false;
    return this.status === 'loaded';
  }

  setWaveformWidth(width: number) {
    this.canvasWrapper.setWaveformWidth(width);
  }

  setBackgroundColor(color: string) {
    this.canvasWrapper.setBackgroundColor(color);
  }

  setWaveformColor(color: string) {
    this.canvasWrapper.setWaveformColor(color);
  }

  setPlayedWaveformWidth(width: number) {
    this.canvasWrapper.setPlayedWaveformWidth(width);
  }

  setPlayedWaveformColor(color: string) {
    this.canvasWrapper.setPlayedWaveformColor(color);
  }

  setCanvasWidth(width: number) {
    this.canvasWrapper.setCanvasWidth(width);
  }

  setCanvasHeight(height: number) {
    this.canvasWrapper.setCanvasHeight(height);
  }

  getCanvasWidth() {
    return this.canvasWrapper.getCanvasWidth();
  }

  getCanvasHeight() {
    return this.canvasWrapper.getCanvasHeight();
  }

  appendOnStatusUpdateCallback(callback: (status: AudioPlayerStatus) => void) {
    this.onStatusUpdateCallbacks.add(callback);
  }

  appendOnTimeUpdateCallback(callback: (time: number) => void) {
    this.onTimeUpdateCallbacks.add(callback);
  }

  appendOnPlaybackStatusUpdateCallback(callback: (isPlaying: boolean) => void) {
    this.onPlaybackStatusUpdateCallbacks.add(callback);
  }

  removeOnPlaybackStatusUpdateCallback(callback: (isPlaying: boolean) => void) {
    this.onPlaybackStatusUpdateCallbacks.delete(callback);
  }

  removeOnStatusUpdateCallback(callback: (status: AudioPlayerStatus) => void) {
    this.onStatusUpdateCallbacks.delete(callback);
  }

  removeOnTimeUpdateCallback(callback: (time: number) => void) {
    this.onTimeUpdateCallbacks.delete(callback);
  }

  destroy() {
    if (this.destroyed) return;
    this.canvasWrapper.destroy();
    this.onPlaybackStatusUpdateCallbacks.clear();
    this.onTimeUpdateCallbacks.clear();
    this.onStatusUpdateCallbacks.clear();
    if (this.audio) {
      this.detachAudioListeners(this.audio);
      this.audio = null;
      this.track = null;
      this.audioContext?.close().then(() => (this.destroyed = true));
    }
  }
}
